import {
  TriggerTargetType,
  type TriggerRecord,
} from "@/shared/domain/triggers";
import { Goto } from "@/shared/navigation";

/**
 * Tabs of the trigger detail page. Every trigger shows all of them; the ones
 * whose content comes from a kind of target the trigger lacks are greyed out
 * with the reason, so a reader learns what a tab would hold rather than
 * wondering where it went.
 */
export type TriggerDetailTabName =
  "details" | "history" | "threads" | "invocations";

export const TRIGGER_DETAIL_TAB_LABELS: Record<TriggerDetailTabName, string> = {
  details: "Details",
  history: "Evaluation history",
  threads: "Threads",
  invocations: "Invocations",
};

/**
 * The tab strip in order. The first group is the trigger itself and what it
 * did; the second is what those runs produced, one click away, drawn from
 * the invocations and agents surfaces.
 */
export const TRIGGER_DETAIL_TAB_GROUPS: readonly (readonly TriggerDetailTabName[])[] =
  [
    ["details", "history"],
    ["invocations", "threads"],
  ];

export interface TriggerDetailTab {
  name: TriggerDetailTabName;
  /** Why the tab has nothing to show for this trigger, or null when it does. */
  unavailableReason: string | null;
}

/** Every tab, with whether this trigger's targets give it anything to show. */
export function triggerDetailTabs(record: TriggerRecord): TriggerDetailTab[] {
  const invokesAnAction = record.targets.some(
    (target) => target.type === TriggerTargetType.InvokeAction,
  );
  const startsAnAgent = record.targets.some(
    (target) => target.type === TriggerTargetType.StartAgent,
  );
  return [
    { name: "details", unavailableReason: null },
    { name: "history", unavailableReason: null },
    {
      name: "invocations",
      unavailableReason: invokesAnAction
        ? null
        : "Lists the invocations this trigger started. Shown once it has an invoke-action target.",
    },
    {
      name: "threads",
      unavailableReason: startsAnAgent
        ? null
        : "Lists the agent threads this trigger started. Shown once it has a start-agent target.",
    },
  ];
}

/**
 * URL of one tab of a trigger's detail page. The `details` tab is the page's
 * index, at the trigger's route with no tab segment, so every link built by
 * `Goto.createTriggerRoute` opens on it.
 */
export function triggerDetailPath(
  triggerId: string,
  name: string,
  tab: TriggerDetailTabName,
): string {
  const base = Goto.createTriggerRoute(triggerId, name);
  return tab === "details" ? base : `${base}/${tab}`;
}
